/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx, keyframes } from '@compiled/react';

const skeletonPulse = keyframes({
	'0%, 100%': { opacity: 1 },
	'50%': { opacity: 0.4 },
});

const skeletonCircleStyles = css({
	animationName: skeletonPulse,
	animationDuration: '1.5s',
	animationTimingFunction: 'ease-in-out',
	animationIterationCount: 'infinite',
});

const skeletonLineStyles = css({
	strokeWidth: 2,
	animationName: skeletonPulse,
	animationDuration: '1.5s',
	animationTimingFunction: 'ease-in-out',
	animationIterationCount: 'infinite',
});

const SKELETON_NODES = [
	{ cx: 200, cy: 150, r: 24, delay: '0s' },
	{ cx: 100, cy: 260, r: 20, delay: '0.2s' },
	{ cx: 320, cy: 260, r: 20, delay: '0.4s' },
	{ cx: 80, cy: 130, r: 18, delay: '0.6s' },
	{ cx: 340, cy: 100, r: 18, delay: '0.8s' },
];

const SKELETON_EDGES = [
	{ x1: 200, y1: 150, x2: 100, y2: 260, delay: '0.1s' },
	{ x1: 200, y1: 150, x2: 320, y2: 260, delay: '0.3s' },
	{ x1: 200, y1: 150, x2: 80, y2: 130, delay: '0.5s' },
	{ x1: 200, y1: 150, x2: 340, y2: 100, delay: '0.7s' },
];

export interface LoadingStateIllustrationProps {
	/** Token-resolved color for skeleton edges (lines). Use color.skeleton.subtle token. */
	edgeColor: string;
	/** Token-resolved color for skeleton nodes (circles). Use color.skeleton token. */
	nodeColor: string;
}

export function LoadingStateIllustration({
	nodeColor,
	edgeColor,
}: LoadingStateIllustrationProps): JSX.Element {
	return (
		<svg
			width="420"
			height="360"
			viewBox="0 0 420 360"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			{SKELETON_EDGES.map((edge, i) => (
				<line
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- css prop required for compiled animation
					css={skeletonLineStyles}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- animation-delay is dynamic and cannot be expressed statically
					style={{ animationDelay: edge.delay, stroke: edgeColor }}
					key={`skel-edge-${i}`}
					x1={edge.x1}
					y1={edge.y1}
					x2={edge.x2}
					y2={edge.y2}
				/>
			))}
			{SKELETON_NODES.map((node, i) => (
				<circle
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- css prop required for compiled animation
					css={skeletonCircleStyles}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- animation-delay is dynamic and cannot be expressed statically
					style={{ animationDelay: node.delay, fill: nodeColor }}
					key={`skel-node-${i}`}
					cx={node.cx}
					cy={node.cy}
					r={node.r}
				/>
			))}
		</svg>
	);
}
